import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  LinearProgress,
  Toolbar,
  Divider,
} from "@mui/material";
import Api, { APIs } from "../../../Api";

// ---- Types ----
export type PsdpProject = {
  sr_no: number | null;
  psdp_no: string | null;
  project_title: string | null;
  approval_authority: string | null;
  approval_date: string | null;
  approved_project_cost_usd_m: number | null;
  psdp_2023_24_allocation_usd_m: number | null;
  psdp_2024_25_allocation_usd_m: number | null;
  psdp_2025_26_allocation_usd_m: number | null;
};

export type PsdpProjectsTableProps = {
  title?: string;
};

const fmt = (n: number | null | undefined) =>
  n === null || n === undefined
    ? "–"
    : n.toLocaleString(undefined, { maximumFractionDigits: 2 });

function computeTotals(rows: PsdpProject[]) {
  const sum = (key: keyof PsdpProject) =>
    rows.reduce(
      (acc, r) => acc + (typeof r[key] === "number" ? (r[key] as number) : 0),
      0,
    );
  return {
    approved: sum("approved_project_cost_usd_m"),
    a2324: sum("psdp_2023_24_allocation_usd_m"),
    a2425: sum("psdp_2024_25_allocation_usd_m"),
    a2526: sum("psdp_2025_26_allocation_usd_m"),
  };
}

// Height for the scrollable region (adjust to taste)
const VIEWPORT_HEIGHT = "70vh";

export default function PsdpProjectsTable({
  title = "Post Flood-2022 Recovery & Resilient Development",
}: PsdpProjectsTableProps) {
  const [rows, setRows] = useState<PsdpProject[] | null>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const url: string = APIs.MoPD_PSDP_PROJECTS;
    setLoading(true);
    Api.get(url)
      .then((payload) => {
        console.log("PSDP Project", payload);
        if (payload) setRows(payload);
      })
      .catch((e: any) => setError(e?.message || "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  const displayRows = useMemo(() => {
    if (!rows) return [];

    return [...rows].sort((a, b) => {
      const aNo = a.sr_no ?? Number.MAX_SAFE_INTEGER;
      const bNo = b.sr_no ?? Number.MAX_SAFE_INTEGER;

      return aNo - bNo; // Ascending order
    });
  }, [rows]);

  const totals = useMemo(() => computeTotals(displayRows), [displayRows]);

  return (
    <Paper
      elevation={1}
      className="p-4"
      sx={{
        height: "80vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "white",
      }}
    >
      <Toolbar disableGutters sx={{ mb: 1, justifyContent: "space-between" }}>
        <Typography variant="h6" fontWeight={700}>
          {title}
        </Typography>
        <Typography variant="subtitle2" sx={{ opacity: 0.8 }}>
          US$ Million
        </Typography>
      </Toolbar>
      <Divider sx={{ mb: 2 }} />

      {loading && <LinearProgress />}
      {error && (
        <Box sx={{ color: "error.main", mb: 1 }}>Failed to load: {error}</Box>
      )}

      {/* Scrollable table */}
      <TableContainer
        sx={{
          flex: 1,
          overflow: "auto",
          borderRadius: 2,
          border: (t) => `1px solid ${t.palette.divider}`,
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={th}>Sr. No.</TableCell>
              <TableCell sx={th}>PSDP #</TableCell>
              <TableCell sx={thCenter}>Project Title</TableCell>
              <TableCell sx={thCenter}>Approval Authority / Date</TableCell>
              <TableCell sx={thRight}>Approved Project Cost (US$ Mn)</TableCell>
              <TableCell sx={thRight}>
                PSDP 2023-24 Allocation (US$ Mn)
              </TableCell>
              <TableCell sx={thRight}>
                PSDP 2024-25 Allocation (US$ Mn)
              </TableCell>
              <TableCell sx={thRight}>
                PSDP 2025-26 Allocation (US$ Mn)
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayRows.map((r, idx) => (
              <TableRow key={r.sr_no ?? idx} hover>
                <TableCell sx={tdCenter}>{r.sr_no ?? idx + 1}</TableCell>
                <TableCell sx={td}>{r.psdp_no ?? "–"}</TableCell>
                <TableCell sx={{ ...td, fontWeight: 600 }}>
                  {r.project_title ?? "–"}
                </TableCell>
                <TableCell sx={tdCenter}>
                  {r.approval_authority} <br />
                  {r.approval_date ?? "–"}
                </TableCell>
                <TableCell sx={tdRight}>
                  {fmt(r.approved_project_cost_usd_m)}
                </TableCell>
                <TableCell sx={tdRight}>
                  {fmt(r.psdp_2023_24_allocation_usd_m)}
                </TableCell>
                <TableCell sx={tdRight}>
                  {fmt(r.psdp_2024_25_allocation_usd_m)}
                </TableCell>
                <TableCell sx={tdRight}>
                  {fmt(r.psdp_2025_26_allocation_usd_m)}
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell sx={totalCell} colSpan={4}>
                Total US$ Million
              </TableCell>
              <TableCell sx={totalNum}>{fmt(totals.approved)}</TableCell>
              <TableCell sx={totalNum}>{fmt(totals.a2324)}</TableCell>
              <TableCell sx={totalNum}>{fmt(totals.a2425)}</TableCell>
              <TableCell sx={totalNum}>{fmt(totals.a2526)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Footer fixed below */}
      <Box
        sx={{
          mt: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="caption" sx={{ opacity: 0.8 }}>
          Updated on: June 27, 2025
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.8 }}>
          Source: RRU / MoPD&SI
        </Typography>
      </Box>
    </Paper>
  );
}

// ---- Cell styles (MUI SX) ----
const base = {
  borderRight: (t: any) => `1px solid ${t.palette.divider}`,
  borderBottom: (t: any) => `1px solid ${t.palette.divider}`,
};
const th = {
  ...base,
  // backgroundColor: (t: any) => t.palette.success.light + "22",
  backgroundColor: (t: any) => t.palette.success.light + "dd",
  fontWeight: 700,
  whiteSpace: "nowrap",
  position: "sticky",
  top: 0,
  zIndex: 2,
};
const thRight = { ...th, textAlign: "right" };
const thCenter = { ...th, textAlign: "center" };
const td = { ...base };
const tdRight = { ...td, textAlign: "right" };
const tdCenter = { ...td, textAlign: "center" };
const totalCell = {
  ...base,
  fontWeight: 800,
  textAlign: "right",
  backgroundColor: (t: any) => t.palette.grey[100],
};
const totalNum = { ...totalCell, width: 140 };
